import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Switch, FormControlLabel,
  Divider, Alert, MenuItem, CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

const ConfiguracionAgente = () => {
  const [form, setForm] = useState({
    cicloSemanas: 3,
    lunesReferencia: '',
    generarSabados: true,
    generarDomingos: false,
    notas: '',
  });
  const [loading, setLoading]   = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje]   = useState(null);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/configuracion-agente', {
          credentials: 'include',
          headers: { 'x-access-token': token() },
        });
        const data = await res.json();
        if (data) {
          setForm({
            cicloSemanas:    data.cicloSemanas    ?? 3,
            lunesReferencia: data.lunesReferencia ?? '',
            generarSabados:  data.generarSabados  ?? true,
            generarDomingos: data.generarDomingos ?? false,
            notas:           data.notas           ?? '',
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setMensaje(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lunesReferencia) {
      setMensaje({ tipo: 'error', texto: 'El lunes de referencia es obligatorio' });
      return;
    }
    setGuardando(true);
    try {
      const res = await fetch('/api/configuracion-agente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-token': token() },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setMensaje({
        tipo: res.ok ? 'success' : 'error',
        texto: data.message || (res.ok ? 'Configuración guardada' : 'Error al guardar'),
      });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión' });
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SettingsIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Configuración Agente IA Turnos
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>

          {/* Ciclo de rotación */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Rotación de personal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define cada cuántas semanas se rota el grupo (G0, G1, G2) entre los turnos.
          </Typography>
          <TextField
            select
            label="Ciclo de rotación"
            name="cicloSemanas"
            value={form.cicloSemanas}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
          >
            {[1, 2, 3, 4].map(n => (
              <MenuItem key={n} value={n}>{n} {n === 1 ? 'semana' : 'semanas'}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Lunes de referencia (inicio del ciclo)"
            name="lunesReferencia"
            type="date"
            value={form.lunesReferencia}
            onChange={handleChange}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            helperText="Debe ser un lunes. El agente calcula la semana del ciclo a partir de esta fecha."
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Días a cubrir */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Días a programar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Los turnos activos por período se toman del formulario{' '}
            <strong>&quot;Parámetros de Turnos&quot;</strong> ya configurado.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                name="generarSabados"
                checked={form.generarSabados}
                onChange={(e) => setForm(prev => ({ ...prev, generarSabados: e.target.checked }))}
                color="primary"
              />
            }
            label="Incluir sábados en la programación"
            sx={{ mb: 2, display: 'block' }}
          />

          <FormControlLabel
            control={
              <Switch
                name="generarDomingos"
                checked={form.generarDomingos}
                onChange={(e) => setForm(prev => ({ ...prev, generarDomingos: e.target.checked }))}
                color="warning"
              />
            }
            label="Generar asignaciones en domingos"
            sx={{ mb: 3, display: 'block' }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Notas */}
          <TextField
            label="Notas internas (opcional)"
            name="notas"
            value={form.notas}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 3 }}
            placeholder="Ej: Ciclo iniciado el 30/Mar/2026 por cambio de jornada"
          />

          {mensaje && (
            <Alert severity={mensaje.tipo} sx={{ mb: 2 }}>{mensaje.texto}</Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {guardando ? 'Guardando...' : 'Guardar configuración'}
          </Button>
        </Box>
      </Paper>

      {/* Resumen del estado actual */}
      <Paper sx={{ p: 2, mt: 3, backgroundColor: 'rgba(0,0,0,0.03)' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Estado actual del agente
        </Typography>
        <Typography variant="body2">
          • Ciclo: <strong>{form.cicloSemanas} semana(s)</strong>
        </Typography>
        <Typography variant="body2">
          • Lunes de referencia: <strong>{form.lunesReferencia || 'No definido'}</strong>
        </Typography>
        <Typography variant="body2">
          • Sábados: <strong>{form.generarSabados ? 'Incluidos' : 'Excluidos'}</strong>
        </Typography>
        <Typography variant="body2">
          • Domingos: <strong>{form.generarDomingos ? 'Con asignación' : 'Sin asignación'}</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: 12 }}>
          Los turnos activos y sus horarios se leen del formulario &quot;Parámetros de Turnos&quot;.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ConfiguracionAgente;
